import { notification } from 'antd'
import { ArgsProps, NotificationInstance } from 'antd/es/notification/interface'
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

type NotificationProps = Partial<Omit<ArgsProps, 'message' | 'description'>>

export type NotificationFunction = (message: string, args?: ArgsProps) => void

export type GeckoNotificationContextType = {
  notificationApi: NotificationInstance
  successNotification: NotificationFunction
  errorNotification: NotificationFunction
  infoNotification: NotificationFunction
  warningNotification: NotificationFunction
}

const DEFAULT_NOTIFICATION_PROPS: NotificationProps = {
  placement: 'topRight',
  duration: 10,
}

const GeckoNotificationContext =
  createContext<GeckoNotificationContextType | null>(null)

export const useGeckoNotifications: () => GeckoNotificationContextType = () => {
  const context = useContext(GeckoNotificationContext)
  if (!context || context.notificationApi === null) {
    console.error(
      'Notifications context not configured - please place a GeckoNotificationProvider at the top level of your page.'
    )
    throw new Error(
      'useGeckoNotifications must be used within a GeckoNotificationProvider'
    )
  }
  return context
}

export const GeckoNotificationProvider: FC<{
  children: ReactNode
  notificationProps?: NotificationProps
}> = ({ children, notificationProps }) => {
  const [api, contextHolder] = notification.useNotification()

  const props = useMemo(
    () => notificationProps ?? DEFAULT_NOTIFICATION_PROPS,
    [notificationProps]
  )

  const successNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) => {
      const resolvedProps = overrideProps ?? props
      api.success({
        title: overrideProps?.title ?? 'Success',
        message,
        ...resolvedProps,
      })
    },
    [api, props]
  )

  const errorNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) => {
      console.error(message)
      api.error({
        title: overrideProps?.title ?? message,
        message,
        ...(overrideProps ?? props),
      })
    },
    [api, props]
  )

  const infoNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) =>
      api.info({
        title: overrideProps?.title ?? message,
        message,
        ...(overrideProps ?? props),
      }),
    [api, props]
  )

  const warningNotification = useCallback(
    (message: string, overrideProps?: NotificationProps) =>
      api.warning({
        title: overrideProps?.title ?? message,
        message,
        ...(overrideProps ?? props),
      }),
    [api, props]
  )

  return (
    <GeckoNotificationContext.Provider
      value={useMemo(
        () => ({
          notificationApi: api,
          successNotification,
          errorNotification,
          infoNotification,
          warningNotification,
        }),
        [
          api,
          successNotification,
          errorNotification,
          infoNotification,
          warningNotification,
        ]
      )}>
      {contextHolder}
      {children}
    </GeckoNotificationContext.Provider>
  )
}
